import * as XLSX from "xlsx";
import { needsReview } from "./crossLabels";

const NAME_HINTS = [
  "상품명",
  "품명",
  "품목",
  "제품",
  "goods",
  "description",
  "desc",
  "name",
  "item",
  "product",
];
const COMP_HINTS = [
  "성분",
  "조성",
  "재질",
  "함량",
  "composition",
  "material",
  "content",
  "fabric",
];
const SUPPLIER_HINTS = [
  "해외공급자",
  "공급자",
  "supplier",
  "shipper",
  "vendor",
  "maker",
  "manufacturer",
];

/** 헤더에서 상품명(필수)·성분·해외공급자 컬럼 인덱스 추측. */
export function autoDetectColumns(headers) {
  const norm = (headers || []).map((h) =>
    String(h ?? "")
      .trim()
      .toLowerCase(),
  );
  const find = (hints) =>
    norm.findIndex((h) => h && hints.some((k) => h.includes(k.toLowerCase())));
  let nameIdx = find(NAME_HINTS);
  let compIdx = find(COMP_HINTS);
  let supplierIdx = find(SUPPLIER_HINTS);
  if (nameIdx < 0) nameIdx = norm.findIndex((h) => h.length > 0);
  if (compIdx === nameIdx) compIdx = -1;
  if (supplierIdx === nameIdx || supplierIdx === compIdx) supplierIdx = -1;
  return { nameIdx, compIdx, supplierIdx };
}

/** 행렬(AoA)을 잡 items 로. 상품명 공백행 제외, 성분 있으면 "상품명 성분" 결합, srcIndexes 로 원본 연결, cap 상위. */
export function rowsToItems(rows, map, cap = 300) {
  const { nameIdx, compIdx } = map;
  const items = [];
  const srcIndexes = [];
  for (let i = 0; i < (rows || []).length; i++) {
    const r = rows[i];
    const name = String(r?.[nameIdx] ?? "").trim();
    if (!name) continue;
    const comp = compIdx >= 0 ? String(r?.[compIdx] ?? "").trim() : "";
    items.push({ keyword: comp ? `${name} ${comp}` : name });
    srcIndexes.push(i);
  }
  const capped = items.length > cap;
  return {
    items: capped ? items.slice(0, cap) : items,
    srcIndexes: capped ? srcIndexes.slice(0, cap) : srcIndexes,
    total: items.length,
    used: Math.min(items.length, cap),
    capped,
  };
}

const ORIGIN_INCLUDE_HINTS = ["원산지", "coo"];
const ORIGIN_WORD_RE = /\borigin\b/;
const ORIGIN_EXACT_HINTS = ["c/o"];

/** 원산지 컬럼 자동 감지 — includes(원산지/coo) + 단어경계 origin("Original Amount" 오탐 제거) + 정확일치(c/o). 없으면 -1. */
export function detectOriginIdx(headers) {
  const norm = (headers || []).map((h) =>
    String(h ?? "")
      .trim()
      .toLowerCase(),
  );
  let i = norm.findIndex(
    (h) =>
      h &&
      (ORIGIN_INCLUDE_HINTS.some((k) => h.includes(k)) ||
        ORIGIN_WORD_RE.test(h)),
  );
  if (i < 0) i = norm.findIndex((h) => ORIGIN_EXACT_HINTS.includes(h));
  return i;
}

const HSCODE_INCLUDE_HINTS = ["hscode", "hs code", "세번", "hs코드"];

/** 원본세번(원격지 HSCode) 컬럼 감지 — includes(hscode/hs code/세번) + 정확일치(hs). 없으면 -1. */
export function detectHsCodeIdx(headers) {
  const norm = (headers || []).map((h) =>
    String(h ?? "")
      .trim()
      .toLowerCase(),
  );
  let i = norm.findIndex(
    (h) => h && HSCODE_INCLUDE_HINTS.some((k) => h.includes(k)),
  );
  if (i < 0) i = norm.findIndex((h) => h === "hs");
  return i;
}

/** 신고세번 유니크 목록(rates-summary 조회용) — editedHsk(rep) 우선, 빈 값 제외, 첫 등장 순서. */
export function collectDeclaredHsks(results, groups, editedHsk) {
  const out = [];
  const seen = new Set();
  (results || []).forEach((r) => {
    const rep = groups?.[r.idx]?.members?.[0] ?? r.idx;
    const hsk = (editedHsk && editedHsk.get(rep)) || r.finalHsk || "";
    if (hsk && !seen.has(hsk)) {
      seen.add(hsk);
      out.push(hsk);
    }
  });
  return out;
}

/**
 * 신고용 엑셀 AoA — 원본 컬럼 순서 유지, nameIdx 앞에 [신고세번, 원산지, 세율구분, 세율, FTA대상] 삽입.
 * 신고세번=editedHsk(행)??finalHsk. 원산지=원본 원산지 컬럼 값(detectOriginIdx). 세율 3종=ratesByHsk[신고세번].
 * 신고세번 없는 행은 5컬럼 전부 공란. (신고용 다운로드 — 스펙 2026-07-19)
 */
export function buildExportAoa({
  headers,
  rows,
  map,
  resultsBySrc,
  editedHsk,
  ratesByHsk,
}) {
  const nameIdx = Math.max(0, map?.nameIdx ?? 0);
  const originIdx = detectOriginIdx(headers);
  const NEW_HEADERS = ["신고세번", "원산지", "세율구분", "세율", "FTA대상"];
  const insertAt = Math.min(nameIdx, (headers || []).length);
  const outHeaders = [
    ...(headers || []).slice(0, insertAt),
    ...NEW_HEADERS,
    ...(headers || []).slice(insertAt),
  ];
  const out = [outHeaders];
  (rows || []).forEach((r, i) => {
    const hsk =
      (editedHsk && editedHsk.get(i)) ??
      (resultsBySrc && resultsBySrc.get(i)?.finalHsk) ??
      "";
    const rate = hsk && ratesByHsk ? ratesByHsk[hsk] : null;
    const added = hsk
      ? [
          hsk,
          originIdx >= 0 ? String(r?.[originIdx] ?? "") : "",
          rate?.code ?? "",
          rate?.rate ?? "",
          rate?.fta ?? "",
        ]
      : ["", "", "", "", ""];
    out.push([
      ...(r || []).slice(0, insertAt),
      ...added,
      ...(r || []).slice(insertAt),
    ]);
  });
  return out;
}

/** 결과를 표시최종세번별로 집계(내림차순) + 검토필요 수. displayHsk = editedHsk 우선. */
export function groupByHsk(results, srcIndexes, editedHsk) {
  const counts = new Map();
  let reviewCount = 0;
  (results || []).forEach((r) => {
    const src = srcIndexes ? srcIndexes[r.idx] : r.idx;
    const disp = (editedHsk && editedHsk.get(src)) || r.finalHsk || "미분류";
    counts.set(disp, (counts.get(disp) || 0) + 1);
    if (needsReview(r)) reviewCount++;
  });
  const groups = [...counts.entries()]
    .map(([hsk, count]) => ({ hsk, count }))
    .sort(
      (a, b) => b.count - a.count || String(a.hsk).localeCompare(String(b.hsk)),
    );
  return { total: (results || []).length, reviewCount, groups };
}

/** 상품명+성분 중복 그룹핑. items=유니크 keyword만, groups[u].members=그 키워드의 모든 원본행 idx. cap은 유니크 기준. */
export function rowsToDedupItems(rows, map, cap = 300) {
  const { nameIdx, compIdx } = map;
  const order = [];
  const membersByKw = new Map();
  for (let i = 0; i < (rows || []).length; i++) {
    const r = rows[i];
    const name = String(r?.[nameIdx] ?? "").trim();
    const comp = compIdx >= 0 ? String(r?.[compIdx] ?? "").trim() : "";
    // 서버(HandoffController) keyword 규칙 정본과 일치: 개별 trim → 공백 join → trim.
    // 상품명이 공백이어도 성분이 있으면 keyword=성분(둘 다 공백일 때만 제외) — 서버 results idx와 정합 유지.
    const keyword = (name + " " + comp).trim();
    if (!keyword) continue;
    if (!membersByKw.has(keyword)) {
      membersByKw.set(keyword, []);
      order.push(keyword);
    }
    membersByKw.get(keyword).push(i);
  }
  const totalRows = order.reduce((n, k) => n + membersByKw.get(k).length, 0);
  const capped = order.length > cap;
  const usedKw = capped ? order.slice(0, cap) : order;
  const groups = usedKw.map((k) => ({
    keyword: k,
    members: membersByKw.get(k),
  }));
  return {
    items: groups.map((g) => ({ keyword: g.keyword })),
    groups,
    totalRows,
    uniqueCount: order.length,
    used: usedKw.length,
    capped,
  };
}

/** 결과(idx=유니크 순번)를 그룹의 모든 member 원본행에 매핑 → Map<srcRow, result>. */
export function resultsBySrcExpanded(results, groups) {
  const m = new Map();
  (results || []).forEach((r) => {
    const g = groups?.[r.idx];
    if (!g) return;
    (g.members || []).forEach((src) => m.set(src, r));
  });
  return m;
}

/** 좌 패널 집계. perRow=true면 원본행(members.length) 가중, false면 유니크(1/unique). displayHsk=editedHsk(rep) 우선. */
export function groupByHskV2(results, groups, editedHsk, perRow) {
  const counts = new Map();
  let reviewCount = 0;
  let total = 0;
  (results || []).forEach((r) => {
    const g = groups?.[r.idx];
    const n = perRow ? (g ? g.members.length : 1) : 1;
    const rep = g ? g.members[0] : r.idx;
    const disp = (editedHsk && editedHsk.get(rep)) || r.finalHsk || "미분류";
    counts.set(disp, (counts.get(disp) || 0) + n);
    total += n;
    if (needsReview(r)) reviewCount += n;
  });
  const grps = [...counts.entries()]
    .map(([hsk, count]) => ({ hsk, count }))
    .sort(
      (a, b) => b.count - a.count || String(a.hsk).localeCompare(String(b.hsk)),
    );
  return { total, reviewCount, groups: grps };
}

/** .xlsx/.csv ArrayBuffer → 첫 시트의 헤더+행(AoA). (브라우저 검증 — jest 미검증) */
export function readWorkbookRows(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    blankrows: false,
    defval: "",
  });
  return {
    sheetName,
    sheetNames: wb.SheetNames,
    headers: aoa[0] ?? [],
    rows: aoa.slice(1),
  };
}
